import { isEqual, sortBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { useSearch } from '@/hooks/use-search';
import { useSmartUpdate } from '@/hooks/rxdb-smart';

import { ListItem } from '@/components/list-item';
import { SmartList } from '@/components/smart-list';
import { Tab } from '@flintmint/web-shared/components/tab';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Tabs } from '@flintmint/web-shared/components/tabs';
import { Search } from '@flintmint/web-shared/components/search';
import { NoData } from '@flintmint/web-shared/components/no-data';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Button, type ButtonProps } from '@flintmint/web-shared/components/button';

import { toggleObject } from '@/utils/helper-utils';
import { taggableTypes, tagsTypeFilters } from '@/constants/search-filters';

import type { SearchResult } from '@/services/search-service/types';
import type { SearchableDocument } from '@/services/search-service/orama-schema';

import styles from './assign-tag.module.scss';

export const AssignTag = (props: AssignTagProps) => {
  const { tagId, taggedDocs, appearance = 'outline', color = 'dark' } = props;

  const [term, setTerm] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [typeId, setTypeId] = useState(tagsTypeFilters[0].id);
  const [selections, setSelections] = useState<SearchableDocument[]>([]);
  const [animationCompleted, setAnimationCompleted] = useState(false);

  const { update: smartUpdate } = useSmartUpdate();

  const { data, isLoading } = useSearch(
    {
      term,
      where: {
        types: typeId === 'all' ? taggableTypes : [typeId],
      },
    },
    enabled
  );

  const initialSelections = taggedDocs.map((doc) => doc.document);

  useEffect(() => {
    if (enabled) {
      setSelections(initialSelections);
      setTimeout(() => setAnimationCompleted(true), 200);
    }
  }, [enabled]);

  useEffect(() => {
    return () => {
      setTerm('');
      setSelections([]);
      setTypeId(tagsTypeFilters[0].id);
    };
  }, []);

  const rowProps = useCallback(
    (searchableDoc: SearchableDocument) => ({
      onClick: () => {
        setSelections((prev) => toggleObject(prev, searchableDoc));
      },
    }),
    []
  );

  const renderRow = useCallback(
    (searchableDoc: SearchableDocument, selected?: boolean) => (
      <ListItem
        multiselectOn
        loading={isLoading}
        active={!!selected}
        data={searchableDoc}
        selected={!!selected}
        showDescription={false}
        displayCheckForSelected
        data-testid="assign-tag-list-item"
      />
    ),
    [isLoading]
  );

  const renderPlaceholder = useCallback(
    () => <ListItem loading multiselectOn selected={false} showDescription={false} data-testid="assign-tag-list-item" />,
    []
  );

  return (
    <Popover
      open={enabled}
      offset={[0, 4]}
      onOpen={() => setEnabled(true)}
      onClose={async () => {
        setAnimationCompleted(false);
        const selectedIds = new Set(selections.map((item) => item.id));
        const initialIds = new Set(initialSelections.map((item) => item.id));

        if (enabled && !isEqual(sortBy([...initialIds]), sortBy([...selectedIds]))) {
          const docsToAdd = selections.filter((doc) => !initialIds.has(doc.id));
          const docsToRemove = initialSelections.filter((doc) => !selectedIds.has(doc.id));

          for (const doc of docsToAdd) {
            await smartUpdate(doc, {
              tagIds: [...(doc.tagIds || []), tagId!],
            });
          }

          for (const doc of docsToRemove) {
            await smartUpdate(doc, {
              tagIds: doc.tagIds.filter((id) => id !== tagId),
            });
          }
        }

        setEnabled(false);
      }}
      content={
        enabled && (
          <div className={styles.assignTag}>
            <Search
              placeholder="Search items"
              size="extra-large"
              appearance="underline"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Tabs
              value={typeId}
              appearance="simple"
              onChange={setTypeId}
              className={styles.filtersHolder}
              indicatorClassName={styles.filterIndicator}
            >
              {tagsTypeFilters.map(({ name, id }) => (
                <Tab id={id} key={id} name={name} role="listitem" />
              ))}
            </Tabs>
            {!animationCompleted || isLoading || !!data.length ? (
              <SmartList<SearchableDocument>
                overscan={30}
                defaultSize={82}
                loading={isLoading || !animationCompleted}
                rowProps={rowProps}
                multiSelectionOn
                data={data.map((item) => item.document) as SearchableDocument[]}
                rowRenderer={renderRow}
                placeholderRenderer={renderPlaceholder}
                selections={selections}
                paddingEnd={8}
              />
            ) : (
              <NoData
                color="darker"
                description={`No Items were\n found for your request`}
                icon={<Icon multiColor type="search-01-bulk" size={60} />}
              />
            )}
          </div>
        )
      }
    >
      <Button color={color} appearance={appearance} icon={<Icon type="tag-02" size={appearance !== 'outline' ? 20 : 24} />}>
        Assign Tag
      </Button>
    </Popover>
  );
};

export interface AssignTagProps {
  tagId?: string;
  taggedDocs: SearchResult[];
  color?: ButtonProps['color'];
  appearance?: ButtonProps['appearance'];
}
